import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ArrowRight, ChevronRight } from "lucide-react";
import { api, inr, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  placed: "bg-blue-50 text-blue-700", confirmed: "bg-indigo-50 text-indigo-700", preparing: "bg-amber-50 text-amber-700",
  shipped: "bg-violet-50 text-violet-700", out_for_delivery: "bg-orange-50 text-orange-700", delivered: "bg-green-50 text-green-700", cancelled: "bg-red-50 text-red-700",
};

export const statusChip = (s) => STATUS_COLORS[s] || "bg-stone-100 text-stone-600";
export const statusLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function OrdersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === false) nav("/auth", { state: { from: "/orders" } });
    if (user) api.get("/orders").then(({ data }) => setOrders(data.items)).catch(() => setOrders([]));
  }, [user, nav]);

  if (!user || orders === null) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-400 text-sm">Loading your orders…</div>;

  if (!orders.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="empty-orders">
        <div className="w-20 h-20 mx-auto rounded-3xl bg-[#F5EFE6] grid place-items-center"><Package className="text-[#B45309]" size={34} /></div>
        <h1 className="font-serif text-3xl font-bold text-stone-900 mt-6">No orders yet</h1>
        <p className="text-stone-500 mt-3">When you send a gift, its journey will appear here.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors" data-testid="empty-orders-explore">
          Start Gifting <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 lg:py-14" data-testid="orders-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">My Orders</h1>
      <div className="space-y-4 mt-8">
        {orders.map((o) => (
          <Link key={o.order_id} to={`/orders/${o.order_id}`} className="block p-5 rounded-2xl bg-white border border-[#E9E2D7] hover:border-[#B45309]/40 transition-colors" data-testid={`order-card-${o.order_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono font-bold text-stone-900 text-sm">{o.order_id}</p>
                <p className="text-xs text-stone-400 mt-0.5">{fmtDate(o.created_at)} · {o.items.length} item{o.items.length > 1 ? "s" : ""}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusChip(o.status)}`}>{statusLabel(o.status)}</span>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex -space-x-3">
                {o.items.slice(0, 3).map((i, idx) => i.image && <img key={idx} src={i.image} alt={i.name} className="w-11 h-11 rounded-xl object-cover border-2 border-white" />)}
              </div>
              <p className="text-sm text-stone-500 flex-1 line-clamp-1">{o.items.map((i) => i.name).join(", ")}</p>
              <span className="font-bold text-stone-900">{inr(o.total)}</span>
              <ChevronRight size={16} className="text-stone-300" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
